/**
 * Hoe een tegenstander in elkaar zit.
 *
 * De AI van het origineel is een stel Excel-bladen die de engine uitrekent. Van de 23
 * bladen verschilt er precies één tussen de zeven tegenstanders — `8_Strategie1.ki` — en
 * dáár zit hun karakter in. `tools/extract-persoonlijkheden.ts` haalt dat blad eruit;
 * dit bestand rekent ermee.
 *
 * De hele weging is één regel:
 *
 * ```
 * score(kaart) = basisvoorkeur[kaart] + gewicht[bouwklasse van de kaart][puntenverschil]
 * ```
 *
 * De basisvoorkeur zegt wat hij van een kaart vindt, los van de stand. De gewichtenrij
 * zegt hoe belangrijk hij een hele *soort* kaarten vindt bij de stand van dit moment — en
 * dat is waar de karakters uiteenlopen. Bij gelijke stand weegt Bernadette riddermacht 15
 * en Eleonore 11; loopt Eleonore vijf punten achter, dan zakt haar riddermacht naar 1 en
 * die van Bernadette naar 12. Zij vecht zich terug, Eleonore bouwt zich terug.
 *
 * Marlene heeft geen blad. Dat is geen gat in de rip maar het origineel: haar beschrijving
 * zegt dat je nooit weet wat ze van plan is, en zij speelt zonder strategie. Een naam
 * zonder profiel valt hier daarom netjes terug op willekeur, en dat is precies goed.
 */
import { z } from 'zod';
import data from './data/persoonlijkheden.json' with { type: 'json' };

/** Het verschil in overwinningspunten waarover de gewichtenrij loopt. */
export const VERSCHIL_MIN = -7;
export const VERSCHIL_MAX = 7;

const RijSchema = z.array(z.number()).length(VERSCHIL_MAX - VERSCHIL_MIN + 1);

export const ProfielSchema = z
  .object({
    /** Onder welk tekort hij pas iets dúrft te vragen (Forderungsgrenze). */
    vraaggrens: z.number(),
    /** Hoeveel hij over moet houden voordat hij iets weggeeft (Abgabegrenze). */
    afgeefgrens: z.number(),
    /**
     * `Agressiv` uit het blad — meegenomen, maar bewust nog niet gebruikt.
     *
     * De voor de hand liggende lezing is "speelt hij aanvalskaarten", en die klopt niet.
     * Chi staat op 0 maar waardeert de Rooftocht met 55, en Eleonore staat op 0 terwijl ze
     * de Spion met 59 waardeert. Wat de knop dan wél doet, is uit dit blad niet af te
     * lezen. Er iets van maken zou gokken zijn, en de weerzin die je wél kunt meten staat
     * al in de voorkeuren: Eleonore staat op −100 bij zeven van de negen ridders en bij
     * elke geweldkaart. Niet bij álle ridders — Jan (+50) en Willem (+21) mag ze wel, en
     * in de doormeting van 29 juli 2026 bouwt ze over zestig potjes uitsluitend die twee.
     * Zie `kennisbank/05-open-vragen.md`.
     */
    agressief: z.boolean(),
    /** Bouwklasse (1-5) → gewicht per puntenverschil, van −7 tot +7. */
    gewichten: z.record(z.string(), RijSchema),
    /** Kaart-ID → basisvoorkeur. */
    voorkeur: z.record(z.string(), z.number()),
  })
  .strict();

export type Profiel = z.infer<typeof ProfielSchema>;

const BestandSchema = z
  .object({
    bron: z.string(),
    /** Kaart-ID → bouwklasse. Hoort bij de kaart, dus gelijk voor alle zeven. */
    klassen: z.record(z.string(), z.number()),
    karakters: z.record(z.string(), ProfielSchema),
  })
  .strict();

const bestand = BestandSchema.parse(data);

/** Kaart-ID → bouwklasse 1..6. Klasse 6 zijn de aanvalskaarten; die hebben geen as. */
export const BOUWKLASSEN: Readonly<Record<string, number>> = bestand.klassen;

/** De aanvalskaarten staan in een klasse zonder eigen gewichtenrij. */
export const KLASSE_AANVAL = 6;

/** De zeven profielen, op naam van de tegenstander. */
export const PERSOONLIJKHEDEN: Readonly<Record<string, Profiel>> = bestand.karakters;

/**
 * Het profiel bij een naam, of `undefined`.
 *
 * `undefined` is een geldig antwoord, geen fout: Marlene hoort geen profiel te hebben, en
 * een potje uit een oudere opslag kan een naam dragen die we niet kennen. De beller valt
 * dan terug op willekeur.
 */
export function persoonlijkheidVoor(naam: string): Profiel | undefined {
  return PERSOONLIJKHEDEN[naam];
}

/**
 * Wat vindt deze tegenstander op dit moment van deze kaart?
 *
 * `puntenverschil` is zijn stand min die van de ander; hij wordt afgekapt op −7…+7, want
 * verder loopt de tabel niet. Een kaart die het blad niet noemt — de zeven
 * gebeurteniskaarten — krijgt nul: die kies je nooit zelf, dus er valt niets te wegen.
 */
export function kaartscore(profiel: Profiel, cardId: number, puntenverschil: number): number {
  const sleutel = String(cardId);
  const basis = profiel.voorkeur[sleutel];
  if (basis === undefined) return 0;

  const klasse = BOUWKLASSEN[sleutel];
  const rij = klasse === undefined ? undefined : profiel.gewichten[String(klasse)];
  if (rij === undefined) return basis;

  const geknipt = Math.min(VERSCHIL_MAX, Math.max(VERSCHIL_MIN, Math.round(puntenverschil)));
  return basis + (rij[geknipt - VERSCHIL_MIN] ?? 0);
}

/** Is dit een aanvalskaart? Die laat een vreedzaam karakter links liggen. */
export function isAanvalskaart(cardId: number): boolean {
  return BOUWKLASSEN[String(cardId)] === KLASSE_AANVAL;
}
